// sec/src/pages/CRM/HelpDesk/TicketList.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Button, Chip, Avatar, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, MenuItem, Select,
  FormControl, InputLabel, CircularProgress, Alert, Tooltip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../../utils/permissionUtils';
import { ticketService } from './helpDeskService';
import {
  TicketStatusLabels, TicketStatusColors, TicketStatusOptions,
  TicketPriorityLabels, TicketPriorityColors, TicketPriorityOptions,
  formatDateTime, formatTimeAgo
} from './helpDeskEnums';
import TicketForm from './TicketForm';

const TicketList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    view: searchParams.get('view') || 'all'
  });

  // Dialog states
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === 'true');
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Tab for view filter
  const [tabValue, setTabValue] = useState(filters.view === 'my' ? 1 : filters.view === 'unassigned' ? 2 : filters.view === 'overdue' ? 3 : 0);

  useEffect(() => {
    document.title = 'Tickets | Help Desk';
    checkPermissions();
  }, []);

  useEffect(() => {
    if (canView) {
      loadTickets();
    }
  }, [canView, chatbotId, pagination.page, pagination.per_page, searchTerm, filters]);

  const checkPermissions = async () => {
    try {
      const viewPerm = await hasPermission('crm', 'view');
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanView(viewPerm);
      setCanModify(modifyPerm);
      if (!viewPerm) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error checking permissions:', err);
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        per_page: pagination.per_page,
        search: searchTerm || undefined,
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        view: filters.view || undefined
      };

      if (chatbotId) params.chatbot_id = chatbotId;

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await ticketService.getAll(params);
      if (response.success) {
        setTickets(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 0
        }));
      }
    } catch (err) {
      enqueueSnackbar('Failed to load tickets', { variant: 'error' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const views = ['all', 'my', 'unassigned', 'overdue'];
    setFilters(prev => ({ ...prev, view: views[newValue] }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({ status: '', priority: '', view: 'all' });
    setSearchTerm('');
    setTabValue(0);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({ ...prev, per_page: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleFormClose = (refresh = false) => {
    setFormOpen(false);
    setSelectedTicket(null);
    searchParams.delete('new');
    setSearchParams(searchParams);
    if (refresh) {
      loadTickets();
    }
  };

  const getSLAIcon = (slaStatus) => {
    if (!slaStatus) return null;
    switch (slaStatus.status) {
      case 'breached':
        return <Tooltip title="SLA Breached"><ErrorIcon color="error" fontSize="small" /></Tooltip>;
      case 'warning':
        return <Tooltip title="SLA At Risk"><WarningIcon color="warning" fontSize="small" /></Tooltip>;
      case 'met':
      case 'on_track':
        return <Tooltip title="SLA On Track"><CheckIcon color="success" fontSize="small" /></Tooltip>;
      default:
        return null;
    }
  };

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: { xs: 2, sm: 10 }, mx: 3 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view tickets.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk`)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Support Tickets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pagination.total} total tickets
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={loadTickets} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          {canModify && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
            >
              New Ticket
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs for Quick Filters */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="All Tickets" />
          <Tab label="My Tickets" />
          <Tab label="Unassigned" />
          <Tab label="Overdue" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {TicketStatusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                label="Priority"
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {TicketPriorityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              fullWidth
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tickets Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>SLA</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/helpdesk/tickets/${ticket.id}`)}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600} color="primary">
                        {ticket.ticket_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {ticket.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TicketStatusLabels[ticket.status]}
                        color={TicketStatusColors[ticket.status]}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TicketPriorityLabels[ticket.priority]}
                        color={TicketPriorityColors[ticket.priority]}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ticket.contact?.full_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {ticket.assigned_user_name || 'Unassigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getSLAIcon(ticket.sla_status)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(ticket.created_at)}>
                        <Typography variant="body2" color="text.secondary">
                          {formatTimeAgo(ticket.created_at)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.per_page}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Ticket Form Dialog */}
      <TicketForm
        open={formOpen}
        onClose={handleFormClose}
        ticket={selectedTicket}
        chatbotId={chatbotId}
      />
    </Box>
  );
};

export default TicketList;
